import { useState, useEffect } from "react";
import { useAppTranslation } from "@/contexts/TranslationContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Search, Filter, Clock, MapPin, Users, Eye, Zap, Phone, Mail, Home, Calendar, FileText, Edit, AlertCircle, Trash2, Shield, Brain, TrendingUp, Target } from "lucide-react";
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';

import { config as productConfig } from '@/src/config';
interface Referral {
  id: string | number;
  referralNumber?: string; // Unique 10-digit referral number (camelCase from frontend)
  referral_number?: string; // Unique 10-digit referral number (snake_case from backend)
  // Member Information
  memberMedicaidId: string;
  memberName: string;
  dateOfBirth: string;
  zipCode: string;
  preferredLanguage: string;

  // Contact Information
  phoneNumber?: string;
  email?: string;

  // Address Information
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;

  // Health Plan Information
  healthPlan: string;
  planType: "Managed Care" | "Fee-for-Service";

  // Provider Information
  referringProvider: {
    name: string;
    npi: string;
    organization: string;
    phone: string;
  };

  // Clinical Context
  primaryDiagnosis: string;
  riskScore?: number;
  sdohNeeds: string[];
  housingSubcategories?: string[]; // NEW
  transportationSubcategories?: string[]; // NEW
  urgencyLevel: "Immediate" | "Within 24h" | "Within 1 Week" | "Routine";

  // Additional Fields (NEW)
  availability?: string;
  additionalNotes?: string;

  // Authorization & Compliance
  authorizationRequired: boolean;
  consentObtained: boolean;
  hipaaCompliant: boolean;

  // Legacy fields for backward compatibility
  timeline: string;
  status: "Pending" | "Matched" | "Auto-Matched" | "Accepted" | "Authorized" | "In Progress" | "On Hold" | "Completed";
  createdAt: string;
  timeAgo?: string;

  // Processing method
  useMatchingEngine?: boolean;

  // Acceptance Workflow (PHI Integration)
  matchedProviderId?: string;
  acceptanceStatus?: 'pending' | 'accepted' | 'rejected' | 'expired';
  acceptedAt?: string;
  rejectedAt?: string;
  expiresAt?: string;
  phiEmailSent?: boolean;
  emailSentTo?: string;

  // Enrollment fields
  isEnrolled?: boolean;
  enrollmentId?: number;
  enrollmentStatus?: string;
  programName?: string;
  programId?: number;
  memberId?: number;
}

interface ReferralQueueProps {
  referrals: Referral[];
  onCreateNew: () => void;
  onNavigateToLiveFeed?: () => void;
  onViewMatched: (referralId: string) => void;
  onBack?: () => void;
  onUpdateStatus?: (referralId: string, newStatus: string) => void;
  onEditReferral?: (referralId: string) => void;
  onDeleteReferral?: (referralId: string) => void;
  onEnrollMember?: (referralId: string, memberId: number, programId: number, memberName: string) => void;
}

// Helper function to extract numeric ID from referral
const getDisplayReferralNumber = (referral: Referral): string => {
  if (referral.referralNumber) return referral.referralNumber;
  // Extract only digits from the ID (convert to string first to handle integer IDs)
  const idString = String(referral.id);
  const digits = idString.split('').filter(char => char >= '0' && char <= '9').join('');
  return digits.slice(0, 10) || idString.slice(0, 10);
};

export function ReferralQueue({ referrals, onCreateNew, onNavigateToLiveFeed, onViewMatched, onBack, onUpdateStatus, onEditReferral, onDeleteReferral, onEnrollMember }: ReferralQueueProps) {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timelineFilter, setTimelineFilter] = useState("all");
  const [processingMethodFilter, setProcessingMethodFilter] = useState("all");
  const [aiPredictions, setAiPredictions] = useState<Record<string, { matchScore: number; successRate: number }>>({});

  // Fetch AI predictions for referrals
  useEffect(() => {
    const fetchAIPredictions = async () => {
      if (referrals.length === 0) return;

      try {
        const API_URL = productConfig.weaveinApi.baseUrl ?? 'http://localhost:8000';

        // Fetch AI tasks for predictive analytics on referrals
        const response = await authFetch(`${API_URL}/api/ai-orchestration/tasks?category=predictive_analytics&limit=100`);

        if (response.ok) {
          const data = await response.json();
          const predictionsMap: Record<string, { matchScore: number; successRate: number }> = {};

          // Map AI predictions to referrals
          referrals.forEach(referral => {
            const referralTasks = data.tasks?.filter((task: any) =>
              task.related_entity_type === 'referral' && task.related_entity_id?.toString() === referral.id.toString()
            ) || [];

            if (referralTasks.length > 0) {
              const latestTask = referralTasks[0];
              // Normalize confidence score to 0-1 range (handle both 0-1 and 1-10 scales)
              let normalizedScore = latestTask.confidence_score || referral.riskScore || 0.75;
              if (normalizedScore > 1) {
                normalizedScore = normalizedScore / 10; // Convert 1-10 scale to 0-1
              }
              normalizedScore = Math.min(1, Math.max(0, normalizedScore)); // Cap between 0-1

              predictionsMap[referral.id] = {
                matchScore: normalizedScore,
                successRate: normalizedScore * 0.9
              };
            } else {
              // Default predictions based on risk score
              let normalizedRiskScore = referral.riskScore || 0.75;
              if (normalizedRiskScore > 1) {
                normalizedRiskScore = normalizedRiskScore / 10; // Convert 1-10 scale to 0-1
              }
              normalizedRiskScore = Math.min(1, Math.max(0, normalizedRiskScore)); // Cap between 0-1

              predictionsMap[referral.id] = {
                matchScore: normalizedRiskScore,
                successRate: 0.70
              };
            }
          });

          setAiPredictions(predictionsMap);
        }
      } catch (error) {
        console.error('Failed to fetch AI predictions:', error);
      }
    };

    fetchAIPredictions();
  }, [referrals]);

  // Debug: Log referrals when they change
  useEffect(() => {
    if (referrals.length > 0) {
      // SECURITY: Removed PII logging - memberName should not be logged to console
      console.log('🔍 ReferralQueue: Referrals loaded:', referrals.length);
    }
  }, [referrals]);

  // Filter referrals
  const filteredReferrals = referrals.filter(referral => {
    const matchesSearch = referral.memberName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referral.memberMedicaidId.includes(searchTerm) ||
                         referral.zipCode.includes(searchTerm) ||
                         (referral.phoneNumber?.includes(searchTerm) || false) ||
                         (referral.email?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
                         (referral.city?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
                         (referral.state?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
                         referral.primaryDiagnosis.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referral.sdohNeeds.some(need => need.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (referral.housingSubcategories?.some(sub => sub.toLowerCase().includes(searchTerm.toLowerCase())) || false) ||
                         (referral.transportationSubcategories?.some(sub => sub.toLowerCase().includes(searchTerm.toLowerCase())) || false) ||
                         referral.referringProvider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referral.referringProvider.organization.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || referral.status.toLowerCase() === statusFilter;
    const matchesTimeline = timelineFilter === "all" || referral.urgencyLevel === timelineFilter;
    const matchesProcessingMethod = processingMethodFilter === "all" ||
      (processingMethodFilter === "auto" && referral.useMatchingEngine !== false) ||
      (processingMethodFilter === "manual" && referral.useMatchingEngine === false);

    return matchesSearch && matchesStatus && matchesTimeline && matchesProcessingMethod;
  });

  const pendingCount = referrals.filter(r => r.status === "Pending").length;
  const matchedCount = referrals.filter(r => r.status === "Matched" || r.status === "Authorized" || r.status === "In Progress").length;
  const completedCount = referrals.filter(r => r.status === "Completed").length;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center space-x-3 md:space-x-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
              </button>
            )}
            <div>
              {!tabbed && (
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Users className="h-6 w-6 text-blue-600" />
                {t('referrals.queue', 'Referral Queue')}
              </h1>
              )}
              <p className="text-sm md:text-base text-gray-600 mt-1">{t('referrals.queueDescription', 'Manage and track referral requests')}</p>
            </div>
          </div>

          {/* Action buttons - stack on mobile to prevent overlap */}
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
            <Button
              onClick={onCreateNew}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 active:scale-95 transition-all min-h-[44px]"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm md:text-base">{t('referrals.createReferralButton', 'Create Referral')}</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <div className="bg-blue-50 rounded-lg p-3 md:p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">{t('referrals.published', 'Published')}</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-blue-600">{referrals.length}</p>
              <p className="text-xs text-gray-500 mt-1">{t('referrals.totalReferrals', 'Total referrals')}</p>
            </div>

            <div className="bg-orange-50 rounded-lg p-3 md:p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-gray-600">{t('referrals.pending', 'Pending')}</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-orange-600">{pendingCount}</p>
              <p className="text-xs text-gray-500 mt-1">{t('referrals.awaitingAssignment', 'Awaiting assignment')}</p>
            </div>

            <div className="bg-green-50 rounded-lg p-3 md:p-4">
              <div className="flex items-center space-x-2 mb-2">
                <MapPin className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-600">{t('referrals.assigned', 'Assigned')}</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-green-600">{matchedCount + completedCount}</p>
              <p className="text-xs text-gray-500 mt-1">{t('referrals.matchedAndCompleted', 'Matched & completed')}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
          <div className="flex items-center space-x-2 md:space-x-3 mb-4">
            <Search className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
            <h2 className="text-lg md:text-xl font-semibold text-gray-800">{t('referrals.searchAndFilter', 'Search & Filter')}</h2>
          </div>

          <div className="flex flex-col gap-3 md:gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
              <Input
                placeholder={t('referrals.searchPlaceholder', 'Search by name, contact, location...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 md:pl-12 pr-4 h-10 md:h-12 text-sm md:text-base border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-lg"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full h-10 md:h-12 text-sm md:text-base border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-lg">
                  <SelectValue placeholder={t('common.status', 'Status')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">{t('referrals.allStatus', 'All Status')}</SelectItem>
                  <SelectItem value="pending">{t('referrals.pending', 'Pending')}</SelectItem>
                  <SelectItem value="matched">{t('referrals.matched', 'Matched')}</SelectItem>
                  <SelectItem value="authorized">{t('referrals.authorized', 'Authorized')}</SelectItem>
                  <SelectItem value="in progress">{t('referrals.inProgress', 'In Progress')}</SelectItem>
                  <SelectItem value="completed">{t('referrals.completed', 'Completed')}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={timelineFilter} onValueChange={setTimelineFilter}>
                <SelectTrigger className="w-full h-10 md:h-12 text-sm md:text-base border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-lg">
                  <SelectValue placeholder={t('referrals.timeline', 'Timeline')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">{t('referrals.allUrgencyLevels', 'All Urgency Levels')}</SelectItem>
                  <SelectItem value="Immediate">{t('referrals.immediate', 'Immediate')}</SelectItem>
                  <SelectItem value="Within 24h">{t('referrals.within24h', 'Within 24 Hours')}</SelectItem>
                  <SelectItem value="Within 1 Week">{t('referrals.within1Week', 'Within 1 Week')}</SelectItem>
                  <SelectItem value="Routine">{t('referrals.routine', 'Routine')}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={processingMethodFilter} onValueChange={setProcessingMethodFilter}>
                <SelectTrigger className="w-full h-10 md:h-12 text-sm md:text-base border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-lg">
                  <SelectValue placeholder={t('referrals.processingMethod', 'Processing Method')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">{t('referrals.allMethods', 'All Methods')}</SelectItem>
                  <SelectItem value="auto">{t('referrals.autoMatching', 'Auto Matching')}</SelectItem>
                  <SelectItem value="manual">{t('referrals.manualSelection', 'Manual Selection')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Referrals List - Grouped by Member */}
        <div className="space-y-4 md:space-y-6">
          {filteredReferrals.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12">
              <div className="flex flex-col items-center justify-center space-y-4 text-center">
                <Users className="w-12 h-12 md:w-16 md:h-16 text-gray-300" />
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">{t('referrals.noReferralsFound', 'No referrals found')}</h3>
                <p className="text-sm md:text-base text-gray-600 max-w-md">
                  {searchTerm || statusFilter !== "all" || timelineFilter !== "all"
                    ? t('referrals.adjustFilters', 'Try adjusting your filters to see more results.')
                    : t('referrals.getStarted', 'Get started by creating your first referral.')}
                </p>
                <Button
                  onClick={onCreateNew}
                  className="mt-2 bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 transition-all px-6 py-2 rounded-lg min-h-[44px]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('referrals.createFirstReferral', 'Create First Referral')}
                </Button>
              </div>
            </div>
          ) : (
            // Group referrals by member
            Object.entries(
              filteredReferrals.reduce((groups, referral) => {
                const memberKey = `${referral.memberName}_${referral.memberMedicaidId}`;
                if (!groups[memberKey]) {
                  groups[memberKey] = [];
                }
                groups[memberKey].push(referral);
                return groups;
              }, {} as Record<string, Referral[]>)
            ).map(([memberKey, memberReferrals]) => (
              <div key={memberKey} className="space-y-3 md:space-y-4">
                {/* Member Header */}
                <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-center space-x-3 md:space-x-4 flex-1 min-w-0">
                        <div className="w-12 h-12 md:w-14 md:h-14 bg-indigo-100 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                          <Users className="w-6 h-6 md:w-7 md:h-7 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h2 className="text-xl md:text-2xl font-bold text-gray-800 truncate">{memberReferrals[0].memberName}</h2>
                          <div className="grid grid-cols-1 gap-y-2 md:gap-y-3 text-xs md:text-sm text-slate-600 mt-2 md:mt-3">
                            <div className="space-y-2">
                              <div className="flex items-start space-x-2">
                                <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {memberReferrals[0].streetAddress || 'Address not provided'}
                                  </span>
                                  <span className="font-medium text-slate-500">
                                    {memberReferrals[0].city && memberReferrals[0].state
                                      ? `${memberReferrals[0].city}, ${memberReferrals[0].state} ${memberReferrals[0].zipCode}`
                                      : memberReferrals[0].zipCode}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {memberReferrals[0].phoneNumber && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  <span className="font-medium">{memberReferrals[0].phoneNumber}</span>
                                </div>
                              )}
                              {memberReferrals[0].email && (
                                <div className="flex items-center space-x-2">
                                  <Mail className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                  <span className="font-medium truncate max-w-xs">{memberReferrals[0].email}</span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                <span className="font-medium">
                                  {fmt.dateTime(memberReferrals[0].createdAt)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex md:block justify-between md:text-right md:ml-4">
                        <div className="text-xs md:text-sm text-slate-600 mb-1">{t('referrals.totalReferrals', 'Total Referrals')}</div>
                        <div className="text-xl md:text-2xl font-bold text-indigo-600">{memberReferrals.length}</div>
                      </div>
                    </div>
                  </div>

                {/* Individual Referrals for this Member */}
                <div className="space-y-3 md:space-y-4 ml-0 md:ml-4">
                  {memberReferrals.map((referral) => (
                    <div key={referral.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow p-4 md:p-5">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-0 mb-4">
                          <div className="flex items-center space-x-3 lg:space-x-4 min-w-0">
                            <div className="w-10 h-10 bg-blue-100 border-2 border-blue-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                              <Zap className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="text-base md:text-lg font-bold text-gray-800 truncate">
                                Referral #{getDisplayReferralNumber(referral)}
                              </h4>
                              <div className="flex items-center space-x-2 text-xs md:text-sm text-slate-600 mt-1">
                                <Calendar className="w-3 h-3 md:w-4 md:h-4 text-blue-500 flex-shrink-0" />
                                <span className="font-medium truncate">
                                  {fmt.dateTime(referral.createdAt)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 md:gap-3">
                            <Badge
                              variant="secondary"
                              className={`px-3 py-1 text-xs font-semibold rounded-lg ${
                                referral.status === "Pending"
                                  ? "bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border border-orange-300"
                                  : referral.status === "Matched" || referral.status === "Authorized"
                                  ? "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300"
                                  : referral.status === "Auto-Matched"
                                  ? "bg-gradient-to-r from-teal-100 to-cyan-200 text-teal-800 border border-teal-300"
                                  : referral.status === "In Progress"
                                  ? "bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300"
                                  : referral.status === "Completed"
                                  ? "bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300"
                                  : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border border-gray-300"
                              }`}
                            >
                              {referral.status}
                            </Badge>

                            {/* AI Match Score Badge */}
                            {aiPredictions[referral.id] && (
                              <Badge
                                variant="secondary"
                                className="px-3 py-1 text-xs font-semibold rounded-lg bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-800 border border-indigo-300 flex items-center gap-1"
                                title={`AI Match Confidence: ${(aiPredictions[referral.id].matchScore * 100).toFixed(0)}% - This score indicates how well the member's needs align with available providers based on services, location, capacity, and historical outcomes. Higher scores suggest better potential matches.`}
                              >
                                <Target className="w-3 h-3" />
                                {(aiPredictions[referral.id].matchScore * 100).toFixed(0)}% Match
                              </Badge>
                            )}

                            {/* AI Success Rate Badge */}
                            {aiPredictions[referral.id] && (
                              <Badge
                                variant="secondary"
                                className="px-3 py-1 text-xs font-semibold rounded-lg bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 border border-emerald-300 flex items-center gap-1"
                                title="AI-predicted success rate"
                              >
                                <TrendingUp className="w-3 h-3" />
                                {(aiPredictions[referral.id].successRate * 100).toFixed(0)}% Success
                              </Badge>
                            )}
                            <Badge
                              variant="secondary"
                              className={`px-3 py-1 text-xs font-semibold rounded-lg ${
                                referral.useMatchingEngine === false
                                  ? "bg-gradient-to-r from-violet-100 to-violet-200 text-violet-800 border border-violet-300"
                                  : "bg-gradient-to-r from-teal-100 to-teal-200 text-teal-800 border border-teal-300"
                              }`}
                            >
                              {referral.useMatchingEngine === false ? t('referrals.manualSelection', 'Manual Selection') : t('referrals.autoMatching', 'Auto Matching')}
                            </Badge>

                            {/* Acceptance Status Badge (PHI Integration) */}
                            {referral.acceptanceStatus && (
                              <Badge
                                variant="secondary"
                                className={`px-3 py-1 text-xs font-semibold rounded-lg ${
                                  referral.acceptanceStatus === "accepted"
                                    ? "bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 border border-emerald-300"
                                    : referral.acceptanceStatus === "rejected"
                                    ? "bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300"
                                    : referral.acceptanceStatus === "pending"
                                    ? "bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 border border-amber-300"
                                    : "bg-gradient-to-r from-slate-100 to-slate-200 text-slate-800 border border-slate-300"
                                }`}
                              >
                                {referral.acceptanceStatus === "accepted" && "✓ Accepted"}
                                {referral.acceptanceStatus === "rejected" && "✗ Rejected"}
                                {referral.acceptanceStatus === "pending" && "⏳ Pending"}
                                {referral.acceptanceStatus === "expired" && "⏰ Expired"}
                              </Badge>
                            )}

                            {/* PHI Email Status Indicator (PHI Integration) */}
                            {referral.phiEmailSent && (
                              <Badge
                                variant="secondary"
                                className="px-3 py-1 text-xs font-semibold rounded-lg bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300 flex items-center gap-1"
                              >
                                <Shield className="w-3 h-3" />
                                <Mail className="w-3 h-3" />
                                <span>PHI Sent</span>
                              </Badge>
                            )}

                            {/* Enrollment Status Badge */}
                            {referral.isEnrolled && (
                              <Badge
                                variant="secondary"
                                className="px-3 py-1 text-xs font-semibold rounded-lg bg-gradient-to-r from-teal-100 to-teal-200 text-teal-800 border border-teal-300 flex items-center gap-1"
                              >
                                <Users className="w-3 h-3" />
                                <span>Enrolled</span>
                              </Badge>
                            )}

                            {/* Enroll Button - Show only if not enrolled */}
                            {!referral.isEnrolled && referral.memberId && referral.programId && onEnrollMember && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onEnrollMember(referral.id.toString(), referral.memberId!, referral.programId!, referral.memberName)}
                                className="flex items-center space-x-1 md:space-x-2 bg-white/90 backdrop-blur-sm border-teal-300/50 hover:bg-teal-50/80 text-teal-800 shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg px-2 md:px-3 py-1 text-xs min-h-[36px] md:min-h-[auto] active:scale-95"
                              >
                                <Plus className="w-3 h-3 md:w-3.5 md:h-3.5" />
                                <span>Enroll</span>
                              </Button>
                            )}

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onViewMatched(String(referral.id))}
                              className="flex items-center space-x-1 md:space-x-2 bg-white/90 backdrop-blur-sm border-blue-300/50 hover:bg-blue-50/80 text-blue-800 shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg px-2 md:px-3 py-1 text-xs min-h-[36px] md:min-h-[auto] active:scale-95"
                            >
                              <Eye className="w-3 h-3 md:w-3.5 md:h-3.5" />
                              <span>View</span>
                            </Button>
                            {onEditReferral && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onEditReferral(String(referral.id))}
                                className="flex items-center space-x-1 md:space-x-2 bg-white/90 backdrop-blur-sm border-green-300/50 hover:bg-green-50/80 text-green-800 shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg px-2 md:px-3 py-1 text-xs min-h-[36px] md:min-h-[auto] active:scale-95"
                              >
                                <Edit className="w-3 h-3 md:w-3.5 md:h-3.5" />
                                <span>Edit</span>
                              </Button>
                            )}
                            {onDeleteReferral && referral.status === "Pending" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  if (window.confirm(`Are you sure you want to delete this pending referral for ${referral.memberName}?`)) {
                                    onDeleteReferral(String(referral.id));
                                  }
                                }}
                                className="flex items-center space-x-1 md:space-x-2 bg-white/90 backdrop-blur-sm border-red-300/50 hover:bg-red-50/80 text-red-800 shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg px-2 md:px-3 py-1 text-xs min-h-[36px] md:min-h-[auto] active:scale-95"
                              >
                                <Trash2 className="w-3 h-3 md:w-3.5 md:h-3.5" />
                                <span>Delete</span>
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          {/* Key Information Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                              <div className="flex items-center space-x-2 mb-2">
                                <Users className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                <span className="text-slate-700 font-semibold text-xs">{t('referrals.healthPlan', 'Health Plan')}</span>
                              </div>
                              <p className="text-gray-900 font-bold text-sm ml-6">{referral.healthPlan}</p>
                              <p className="text-gray-600 text-xs ml-6">{referral.planType}</p>
                            </div>
                            <div className={`p-4 rounded-lg border ${
                              referral.urgencyLevel === "Immediate"
                                ? "bg-red-50 border-red-200"
                                : referral.urgencyLevel === "Within 24h"
                                ? "bg-orange-50 border-orange-200"
                                : referral.urgencyLevel === "Within 1 Week"
                                ? "bg-yellow-50 border-yellow-200"
                                : "bg-green-50 border-green-200"
                            }`}>
                              <div className="flex items-center space-x-2 mb-2">
                                <AlertCircle className={`w-4 h-4 flex-shrink-0 ${
                                  referral.urgencyLevel === "Immediate" ? "text-red-600" :
                                  referral.urgencyLevel === "Within 24h" ? "text-orange-600" :
                                  referral.urgencyLevel === "Within 1 Week" ? "text-yellow-600" :
                                  "text-green-600"
                                }`} />
                                <span className="text-slate-700 font-semibold text-xs">{t('referrals.urgencyLevel', 'Urgency Level')}</span>
                              </div>
                              <p className="text-gray-900 font-bold text-sm ml-6">{referral.urgencyLevel}</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                              <div className="flex items-center space-x-2 mb-2">
                                <FileText className="w-4 h-4 text-purple-600 flex-shrink-0" />
                                <span className="text-slate-700 font-semibold text-xs">{t('referrals.referringProvider', 'Referring Provider')}</span>
                              </div>
                              <p className="text-gray-900 font-bold text-sm ml-6">{referral.referringProvider.name}</p>
                              <p className="text-gray-600 text-xs ml-6">{referral.referringProvider.organization}</p>
                            </div>
                          </div>

                          {/* Service Needs - Categorized */}
                          <div className="space-y-3">
                            <span className="text-slate-700 font-bold text-sm">{t('referrals.servicesRequested', 'Services Requested')}:</span>

                            {/* Housing Services */}
                            {referral.housingSubcategories && referral.housingSubcategories.length > 0 && (
                              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <div className="flex items-center space-x-2 mb-3">
                                  <Home className="w-5 h-5 text-green-600 flex-shrink-0" />
                                  <span className="text-green-800 font-semibold text-sm">{t('referrals.housingAssistance', 'Housing Assistance')}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {referral.housingSubcategories.map((sub, idx) => (
                                    <Badge key={`housing-${idx}`} className="bg-green-100 text-green-800 border border-green-300 text-xs px-3 py-1">
                                      {sub}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Transportation Services */}
                            {referral.transportationSubcategories && referral.transportationSubcategories.length > 0 && (
                              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <div className="flex items-center space-x-2 mb-3">
                                  <Zap className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                  <span className="text-blue-800 font-semibold text-sm">{t('referrals.transportationAssistance', 'Transportation Assistance')}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {referral.transportationSubcategories.map((sub, idx) => (
                                    <Badge key={`transport-${idx}`} className="bg-blue-100 text-blue-800 border border-blue-300 text-xs px-3 py-1">
                                      {sub}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Other SDOH Needs */}
                            {referral.sdohNeeds && referral.sdohNeeds.length > 0 && (
                              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                                <div className="flex items-center space-x-2 mb-3">
                                  <Users className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                                  <span className="text-indigo-800 font-semibold text-sm">{t('referrals.otherSocialServices', 'Other Social Services')}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {referral.sdohNeeds.map((need, idx) => (
                                    <Badge key={`sdoh-${idx}`} className="bg-indigo-100 text-indigo-800 border border-indigo-300 text-xs px-3 py-1">
                                      {need}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Availability */}
                          {referral.availability && (
                            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                              <div className="flex items-center space-x-2 mb-2">
                                <Calendar className="w-4 h-4 text-slate-600 flex-shrink-0" />
                                <span className="text-slate-700 font-semibold text-sm">{t('referrals.availability', 'Availability')}</span>
                              </div>
                              <p className="text-gray-800 font-medium text-sm">{referral.availability}</p>
                            </div>
                          )}

                          {/* Additional Notes */}
                          {referral.additionalNotes && (
                            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                              <div className="flex items-center space-x-2 mb-2">
                                <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                <span className="text-amber-800 font-semibold text-sm">{t('referrals.additionalNotes', 'Additional Notes')}</span>
                              </div>
                              <p className="text-gray-800 font-medium text-sm italic">{referral.additionalNotes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}